import {
  AddInvoiceProps,
  GetNumbersAndPercentageBillableResponse,
  GetNumbersAndPercentageForChangeRequestNumbers,
  GetNumbersAndPercentageForProjectNumbers,
  GetNumbersAndPercentageResponse,
  GetNumbersAndPercentageResponseProps,
  InvoiceEdit,
  InvoiceNumber,
  InvoiceSerialNumber,
  InvoiceStatusHistory,
  LookUp,
  ProjectData,
  Props,
  WireTransferDetails,
  getByRoleAPIResponse,
  getByRolePayload,
} from '../../../../../../../types/ProjectManagement/Project/ProjectView/Invoices/AddInvoice/addInvoiceTypes'
import { UpdateInvoice } from '../../../../../../../types/ProjectManagement/Project/ProjectView/Invoices/AddInvoice/editInvoiceTypes'
import { invoiceTracker } from '../../../../../../../types/ProjectManagement/Project/ProjectView/Invoices/invoicesTypes'
import {
  getAuthenticatedRequestConfig,
  useAxios,
} from '../../../../../../../utils/apiUtils'
import {
  AllowedHttpMethods,
  addInvoiceApiConfig,
  projectInvoicesApiConfig,
} from '../../../../../apiList'

const getNumbersAndPercentage = async (
  props: GetNumbersAndPercentageResponseProps,
): Promise<GetNumbersAndPercentageResponse> => {
  const requestConfig = getAuthenticatedRequestConfig({
    url: addInvoiceApiConfig.getNumbersAndPercentage,
    method: AllowedHttpMethods.get,
    params: {
      id: props.id ?? '',
      milestoneid: props.milestoneid,
    },
  })

  const response = await useAxios(requestConfig)
  return response.data
}

const getNumbersAndPercentageBillable = async (
  props: GetNumbersAndPercentageResponseProps,
): Promise<GetNumbersAndPercentageBillableResponse> => {
  const requestConfig = getAuthenticatedRequestConfig({
    url: addInvoiceApiConfig.getNumbersAndPercentage,
    method: AllowedHttpMethods.get,
    params: {
      milestoneid: props.milestoneid,
      id: props.id ?? '',
    },
  })

  const response = await useAxios(requestConfig)
  return response.data
}

const addInvoiceDetails = async (
  addInvoiceProps: AddInvoiceProps,
): Promise<number> => {
  const requestConfig = getAuthenticatedRequestConfig({
    url:
      addInvoiceApiConfig.addInvoice +
      `/?projectid=${addInvoiceProps.projectId}`,
    method: AllowedHttpMethods.post,
    data: addInvoiceProps,
  })
  const response = await useAxios(requestConfig)
  return response.data
}

const projectData = async (projectId: number): Promise<ProjectData> => {
  const requestConfig = getAuthenticatedRequestConfig({
    url: addInvoiceApiConfig.projectData,
    method: AllowedHttpMethods.get,
    params: {
      projectId,
    },
  })

  const response = await useAxios(requestConfig)
  return response.data
}

const lookup = async (): Promise<LookUp> => {
  const requestConfig = getAuthenticatedRequestConfig({
    url: addInvoiceApiConfig.lookup,
    method: AllowedHttpMethods.get,
  })

  const response = await useAxios(requestConfig)
  return response.data
}

const getInvoiceSerialNumber = async (value: Props): Promise<InvoiceNumber> => {
  const requestConfig = getAuthenticatedRequestConfig({
    url:
      addInvoiceApiConfig.getInvoiceSerialNumber +
      `/${value.country}` +
      `/${value.value}`,
    method: AllowedHttpMethods.get,
  })

  const response = await useAxios(requestConfig)
  return response.data
}

const clientInvoiceCountry = async (
  country: string,
  clientName: string,
): Promise<InvoiceSerialNumber> => {
  const requestConfig = getAuthenticatedRequestConfig({
    url:
      addInvoiceApiConfig.clientInvoiceCountry +
      `/${country}` +
      `/${clientName}`,
    method: AllowedHttpMethods.get,
  })

  const response = await useAxios(requestConfig)
  return response.data
}

const invoiceEdit = async (id: number): Promise<InvoiceEdit> => {
  const requestConfig = getAuthenticatedRequestConfig({
    url: addInvoiceApiConfig.invoiceEdit,
    method: AllowedHttpMethods.get,
    params: {
      id,
    },
  })

  const response = await useAxios(requestConfig)
  return response.data
}

const getInvoiceDetails = async (invoiceId: number): Promise<InvoiceEdit> => {
  const requestConfig = getAuthenticatedRequestConfig({
    url: addInvoiceApiConfig.getInvoiceDetails,
    method: AllowedHttpMethods.get,
    params: {
      invoiceId,
    },
  })

  const response = await useAxios(requestConfig)
  return response.data
}

const getNumbersAndPercentageForProjectNumbers = async (
  props: GetNumbersAndPercentageResponseProps,
): Promise<GetNumbersAndPercentageForProjectNumbers> => {
  const requestConfig = getAuthenticatedRequestConfig({
    url: addInvoiceApiConfig.getNumbersAndPercentageForProjectNumbers,
    method: AllowedHttpMethods.get,
    params: {
      id: props.id ?? '',
      milestoneid: props.milestoneid,
    },
  })

  const response = await useAxios(requestConfig)
  return response.data
}

const getNumbersAndPercentageForChangeRequestNumbers = async (
  props: GetNumbersAndPercentageResponseProps,
): Promise<GetNumbersAndPercentageForChangeRequestNumbers> => {
  const requestConfig = getAuthenticatedRequestConfig({
    url: addInvoiceApiConfig.getNumbersAndPercentageForChangeRequestNumbers,
    method: AllowedHttpMethods.get,
    params: {
      id: props.id ?? '',
      milestoneid: props.milestoneid,
    },
  })

  const response = await useAxios(requestConfig)
  return response.data
}

const invoiceCountryBaseApi = async (
  client: string,
  serialNumber: string,
  country: string,
): Promise<boolean> => {
  const requestConfig = getAuthenticatedRequestConfig({
    url:
      addInvoiceApiConfig.invoiceCountryBaseApi +
      `/${client}` +
      `/${serialNumber}` +
      `/${country}`,
    method: AllowedHttpMethods.get,
  })

  const response = await useAxios(requestConfig)
  return response.data
}

const getInvoiceStatusHistoryById = async (
  invoiceId: number,
): Promise<InvoiceStatusHistory[]> => {
  const requestConfig = getAuthenticatedRequestConfig({
    url: addInvoiceApiConfig.getInvoiceStatusHistoryById,
    method: AllowedHttpMethods.get,
    params: {
      invoiceId,
    },
  })

  const response = await useAxios(requestConfig)
  return response.data
}

const deleteInvoiceId = async (id: number): Promise<number | string> => {
  const requestConfig = getAuthenticatedRequestConfig({
    url: addInvoiceApiConfig.deleteInvoiceId,
    method: AllowedHttpMethods.delete,
    params: {
      id,
    },
  })

  const response = await useAxios(requestConfig)
  return response.data
}

const getByRole = async (
  props: getByRolePayload,
): Promise<getByRoleAPIResponse[]> => {
  const requestConfig = getAuthenticatedRequestConfig({
    url: addInvoiceApiConfig.getByRole,
    method: AllowedHttpMethods.post,
    data: props,
  })

  const response = await useAxios(requestConfig)
  return response.data
}

const updateInvoice = async (
  props: UpdateInvoice,
): Promise<UpdateInvoice[]> => {
  const requestConfig = getAuthenticatedRequestConfig({
    url: addInvoiceApiConfig.updateInvoice,
    method: AllowedHttpMethods.put,
    data: props,
  })

  const response = await useAxios(requestConfig)
  return response.data
}

const allowToAdjustInvoice = async (invoiceId: number): Promise<number> => {
  const requestConfig = getAuthenticatedRequestConfig({
    url: addInvoiceApiConfig.allowToAdjustInvoice + `?invoiceId=${invoiceId}`,
    method: AllowedHttpMethods.put,
  })
  const response = await useAxios(requestConfig)
  return response.data
}
const restrictToAdjustInvoice = async (invoiceId: number): Promise<number> => {
  const requestConfig = getAuthenticatedRequestConfig({
    url:
      addInvoiceApiConfig.restrictToAdjustInvoice + `/?invoiceId=${invoiceId}`,
    method: AllowedHttpMethods.put,
    data: invoiceId,
  })
  const response = await useAxios(requestConfig)
  return response.data
}

const checkInvoiceNumber = async (
  client: string,
  serialNumber: number | string,
  country: string,
): Promise<boolean> => {
  const requestConfig = getAuthenticatedRequestConfig({
    url:
      addInvoiceApiConfig.checkInvoiceNumber +
      `/${client}` +
      `/${serialNumber}` +
      `/${country}`,
    method: AllowedHttpMethods.get,
  })

  const response = await useAxios(requestConfig)
  return response.data
}

const checkSerialNumber = async (
  serialNumber: number | string,
  country: string,
  invoiceType: string | undefined,
): Promise<boolean> => {
  const requestConfig = getAuthenticatedRequestConfig({
    url:
      addInvoiceApiConfig.checkSerialNumber +
      `/${serialNumber}` +
      `/${country}` +
      `/${invoiceType}`,
    method: AllowedHttpMethods.get,
  })

  const response = await useAxios(requestConfig)
  return response.data
}

const getWireTransferInstructions = async (
  currency: string,
  bankName: string,
): Promise<WireTransferDetails[]> => {
  const requestConfig = getAuthenticatedRequestConfig({
    url:
      addInvoiceApiConfig.getWireTransferInstructions +
      `/${currency}` +
      `/${bankName}`,
    method: AllowedHttpMethods.get,
  })

  const response = await useAxios(requestConfig)
  return response.data
}

const deletePartialReceivedAmount = async (
  amountId: number,
  invoiceId: number,
): Promise<number> => {
  const requestConfig = getAuthenticatedRequestConfig({
    url: addInvoiceApiConfig.deletePartialReceivedAmount,
    method: AllowedHttpMethods.delete,
    params: {
      amountId,
      invoiceId,
    },
  })

  const response = await useAxios(requestConfig)
  return response.data
}

const checkMilestoneExits = async (
  milestoneid: number,
): Promise<number | boolean | null> => {
  const requestConfig = getAuthenticatedRequestConfig({
    url: addInvoiceApiConfig.checkMilestoneExits,
    method: AllowedHttpMethods.get,
    params: {
      milestoneid,
    },
  })

  const response = await useAxios(requestConfig)
  return response.data
}

const invoiceMileStones = async (projectId: number): Promise<number> => {
  const requestConfig = getAuthenticatedRequestConfig({
    url: addInvoiceApiConfig.invoiceMileStones,
    method: AllowedHttpMethods.get,
    params: {
      projectId,
    },
  })

  const response = await useAxios(requestConfig)
  return response.data
}

const raisedCRPercentage = async (crId: number): Promise<number> => {
  const requestConfig = getAuthenticatedRequestConfig({
    url: addInvoiceApiConfig.raisedCRPercentage,
    method: AllowedHttpMethods.get,
    params: {
      crId,
    },
  })

  const response = await useAxios(requestConfig)
  return response.data
}

type DownloadPDFType = (html: string) => Promise<Blob>

const getInvoicePDF: DownloadPDFType = async (html) => {
  const requestConfig = getAuthenticatedRequestConfig({
    url: projectInvoicesApiConfig.getInvoicePDF,
    method: AllowedHttpMethods.post,
    data: html, // Pass the complete HTML directly
    responseType: 'blob',
    additionalHeaders: {
      'Content-Type': 'text/html',
    },
  })

  const response = await useAxios(requestConfig)
  return response.data
}
type getInvoiceTrackerType = (
  invoiceTrackerProps: invoiceTracker,
) => Promise<Blob>
const getInvoiceTracker: getInvoiceTrackerType = async (
  invoiceTrackerProps: invoiceTracker,
) => {
  const requestConfig = getAuthenticatedRequestConfig({
    url: projectInvoicesApiConfig.invoiceTracker,
    method: AllowedHttpMethods.post,
    params: {
      invoiceId: invoiceTrackerProps.invoiceId,
    },
    data: invoiceTrackerProps.RequestPayload,
    responseType: 'blob',
    additionalHeaders: {
      'Content-Type': 'text/html',
    },
  })
  const response = await useAxios(requestConfig)
  return response.data
}
const addInvoiceApi = {
  getNumbersAndPercentage,
  getNumbersAndPercentageBillable,
  addInvoiceDetails,
  projectData,
  lookup,
  clientInvoiceCountry,
  getInvoiceSerialNumber,
  invoiceEdit,
  getNumbersAndPercentageForProjectNumbers,
  getNumbersAndPercentageForChangeRequestNumbers,
  invoiceCountryBaseApi,
  getInvoiceStatusHistoryById,
  deleteInvoiceId,
  getByRole,
  updateInvoice,
  checkInvoiceNumber,
  getWireTransferInstructions,
  deletePartialReceivedAmount,
  checkSerialNumber,
  checkMilestoneExits,
  invoiceMileStones,
  raisedCRPercentage,
  getInvoiceDetails,
  getInvoicePDF,
  getInvoiceTracker,
  allowToAdjustInvoice,
  restrictToAdjustInvoice,
}

export default addInvoiceApi
